// src/router/AppRouter.jsx
import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

// Auth
import LoginPage from '../components/auth/LoginPage';

// Layout
import MainLayout from '../components/layout/MainLayout';

// Pages
import OverviewPage from '../components/pages/OverviewPage';
import DataPage from '../components/pages/DataPage';
import AnalyticsPage from '../components/pages/AnalyticsPage';
import SchedulePage from '../components/pages/SchedulePage';
import LogsPage from '../components/pages/LogsPage';
import SettingsPage from '../components/pages/SettingsPage';
import WebhooksPage from '../components/pages/WebhooksPage';

// Custom hooks and components
import { useAuth } from '../hooks/useAuth';
import LoadingScreen from '../components/layout/LoadingAnimation';
import ProtectedRoute from './ProtectedRoute';
import { landingPageForRole } from '../utils/rolePermissions';

// The same page set is served at the root and under the /sync-monitor prefix.
// Each entry carries the page key `utils/rolePermissions` gates on, so a role
// that may not open a page is redirected rather than shown an empty screen.
const PAGE_ROUTES = [
  { path: undefined, page: '', element: <OverviewPage /> },
  { path: 'data', page: 'data', element: <DataPage /> },
  { path: 'analytics', page: 'analytics', element: <AnalyticsPage /> },
  { path: 'schedule', page: 'schedule', element: <SchedulePage /> },
  { path: 'logs', page: 'logs', element: <LogsPage /> },
  { path: 'webhooks', page: 'webhooks', element: <WebhooksPage /> },
  { path: 'settings', page: 'settings', element: <SettingsPage /> },
];

const renderPageRoutes = (basePath) =>
  PAGE_ROUTES.map(({ path, page, element }) => {
    const guarded = (
      <ProtectedRoute page={page} basePath={basePath}>
        {element}
      </ProtectedRoute>
    );
    return path === undefined
      ? <Route key="index" index element={guarded} />
      : <Route key={path} path={path} element={guarded} />;
  });

const AppRouter = () => {
  const { isAuthenticated, isAuthLoading, loginError, login, role } = useAuth();

  // Handle login
  const handleLogin = async (credentials) => {
    const success = await login(credentials);
    return success;
  };

  // Loading state while checking auth
  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  const landingPage = landingPageForRole(role);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={!isAuthenticated ?
        <LoginPage onLogin={handleLogin} loginError={loginError} /> :
        <Navigate to={`/${landingPage}`} replace />
      } />

      {/* Support accessing via /sync-monitor prefix */}
      <Route path="/sync-monitor/login" element={!isAuthenticated ?
        <LoginPage onLogin={handleLogin} loginError={loginError} /> :
        <Navigate to={`/sync-monitor/${landingPage}`} replace />
      } />

      {/* Protected nested routes — MainLayout wraps all child pages */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        {renderPageRoutes('')}
      </Route>

      {/* Same routes accessible via /sync-monitor prefix */}
      <Route path="/sync-monitor" element={
        <ProtectedRoute basePath="/sync-monitor">
          <MainLayout />
        </ProtectedRoute>
      }>
        {renderPageRoutes('/sync-monitor')}
      </Route>

      {/* Fallback route */}
      <Route path="*" element={
        <Navigate to={isAuthenticated ? `/${landingPage}` : '/login'} replace />
      } />
    </Routes>
  );
};

export default AppRouter;
